import React, { useEffect, useState } from 'react'
import { getErrorString } from '../../utils/errorUtils'
import './BlueprintSelectorModal.css'

type Props = {
  blueprints: IBlueprint[]
  hiddenBlueprintIds: Set<string>
  canEdit: boolean
  onToggleBlueprint: (blueprintId: string, shouldShow: boolean) => void
  onUpdateBlueprintName: (
    blueprintId: GeneralId,
    name: string | null,
  ) => Promise<IBlueprint>
}

const getBlueprintName = (blueprint: IBlueprint): string => (
  blueprint.name?.trim() || `図面 #${blueprint.id}`
)

const BlueprintSelectorModal = ({
  blueprints,
  hiddenBlueprintIds,
  canEdit,
  onToggleBlueprint,
  onUpdateBlueprintName,
}: Props) => {
  const [currentBlueprints, setCurrentBlueprints] = useState(blueprints)
  const [editingBlueprintId, setEditingBlueprintId] = useState<string | null>(null)
  const [blueprintNameInput, setBlueprintNameInput] = useState('')
  const [savingBlueprintId, setSavingBlueprintId] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    setCurrentBlueprints(blueprints)
  }, [blueprints])

  const startEditing = (blueprint: IBlueprint) => {
    setEditingBlueprintId(String(blueprint.id))
    setBlueprintNameInput(blueprint.name?.trim() ?? '')
    setErrorMessage(null)
  }

  const cancelEditing = () => {
    setEditingBlueprintId(null)
    setBlueprintNameInput('')
    setErrorMessage(null)
  }

  const saveBlueprintName = async (blueprint: IBlueprint) => {
    const blueprintId = String(blueprint.id)
    const normalizedName = blueprintNameInput.trim() || null
    setSavingBlueprintId(blueprintId)
    setErrorMessage(null)

    try {
      const updatedBlueprint = await onUpdateBlueprintName(blueprint.id, normalizedName)
      setCurrentBlueprints((current) => current.map((item) => (
        String(item.id) === blueprintId ? updatedBlueprint : item
      )))
      setEditingBlueprintId(null)
      setBlueprintNameInput('')
    } catch (error: unknown) {
      setErrorMessage(getErrorString(error))
    } finally {
      setSavingBlueprintId(null)
    }
  }

  if (currentBlueprints.length === 0) {
    return (
      <div className="blueprint-selector-modal modal-form">
        図面がありません。
      </div>
    )
  }

  return (
    <div className="blueprint-selector-modal modal-form">
      <div className="blueprint-selector-list">
        {currentBlueprints.map((blueprint) => {
          const blueprintId = String(blueprint.id)
          const blueprintName = getBlueprintName(blueprint)
          const isVisible = !hiddenBlueprintIds.has(blueprintId)
          const isEditing = editingBlueprintId === blueprintId
          const isSaving = savingBlueprintId === blueprintId
          return (
            <div className="blueprint-selector-item" key={blueprintId}>
              <div className="blueprint-selector-controls">
                <div className="blueprint-selector-visibility">
                  <input
                    type="checkbox"
                    defaultChecked={isVisible}
                    aria-label={`${blueprintName}を地図上に表示する`}
                    onChange={(event) => onToggleBlueprint(blueprintId, event.target.checked)}
                  />
                  <span className="blueprint-selector-visibility-label">
                    地図上に表示する
                  </span>
                </div>
                <div className="blueprint-selector-name-row">
                  {isEditing ? (
                    <input
                      id={`blueprint-name-${blueprintId}`}
                      className="blueprint-selector-name-input"
                      type="text"
                      value={blueprintNameInput}
                      maxLength={255}
                      disabled={isSaving}
                      aria-label={`${blueprintName}の図面名`}
                      onChange={(event) => setBlueprintNameInput(event.target.value)}
                    />
                  ) : (
                    <div className="blueprint-selector-label">
                      {blueprintName}
                    </div>
                  )}
                  {canEdit && (
                    <div className="blueprint-selector-name-actions">
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-primary blueprint-selector-save-button"
                            disabled={isSaving}
                            onClick={() => saveBlueprintName(blueprint)}
                          >
                            {isSaving ? '保存中...' : '保存'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-primary blueprint-selector-cancel-button"
                            disabled={isSaving}
                            onClick={cancelEditing}
                          >
                            キャンセル
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-outline-primary blueprint-selector-edit-button"
                          aria-label={`${blueprintName}の名前を編集`}
                          onClick={() => startEditing(blueprint)}
                        >
                          名前を編集
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {isEditing && errorMessage && (
                  <div className="alert alert-danger blueprint-selector-error" role="alert">
                    {errorMessage}
                  </div>
                )}
              </div>
              <img
                className="blueprint-selector-thumb"
                src={blueprint.image.url}
                alt={`${blueprintName}の図面`}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default BlueprintSelectorModal
